import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Target, Star, Users, Shield, Leaf } from 'lucide-react';

const VisionMission = () => {
  const coreValues = [
    {
      icon: Star,
      title: 'Excellence',
      description: 'Quality service without compromise',
    },
    {
      icon: Users,
      title: 'Convenience',
      description: 'Services designed around customer lifestyles',
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'Transparent pricing and honest workmanship',
    },
    {
      icon: Shield,
      title: 'Trust',
      description: 'Responsible handling of every vehicle',
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'Water-efficient and eco-friendly practices',
    },
  ];

  const missionPoints = [
    'Deliver exceptional auto care services with precision, consistency, and attention to detail.',
    'Employ premium, eco-friendly products and best-in-class methods for superior results.',
    'Offer convenient doorstep services that save customers valuable time and effort.',
    'Foster long-term relationships built on trust, transparency, and customer delight.',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="vision-mission-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            Our Purpose
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Vision & Mission
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
            Driving the future of premium car care in Karnataka with our commitment to quality and customer satisfaction.
          </p>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="w-20 h-20 bg-[#1E88E5]/10 rounded-2xl flex items-center justify-center mb-8">
                <Eye className="h-10 w-10 text-[#1E88E5]" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
                Our Vision
              </h2>
              <p className="text-lg text-[#64748B] leading-relaxed">
                To become the most trusted and preferred premium doorstep car care brand, setting new benchmarks in service quality, customer experience, and sustainable auto care.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D] rounded-3xl p-12 text-white">
              <p className="text-2xl font-heading font-semibold leading-relaxed">
                "To be Karnataka's #1 choice for premium, convenient, and eco-friendly car care services."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 bg-white rounded-3xl p-8 shadow-xl">
              <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-6">
                What We Strive For
              </h3>
              <ul className="space-y-4">
                {missionPoints.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-6 h-6 bg-[#F7931E] rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                      <span className="text-white text-sm font-bold">{index + 1}</span>
                    </div>
                    <p className="text-[#64748B]">{point}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2">
              <div className="w-20 h-20 bg-[#F7931E]/10 rounded-2xl flex items-center justify-center mb-8">
                <Target className="h-10 w-10 text-[#F7931E]" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-[#64748B] leading-relaxed">
                To deliver exceptional auto care services with precision and care, while making premium car wash accessible to everyone through our convenient doorstep model.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Our Core Values
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              The principles that guide every decision we make.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 text-center group hover:-translate-y-1"
                data-testid={`core-value-${index}`}
              >
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:bg-[#1E88E5] transition-colors">
                  <value.icon className="h-7 w-7 text-[#1E88E5] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-lg font-bold text-[#0B2C5D] mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-[#64748B]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Be Part of Our Journey
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Experience the premium care that's changing car wash in Karnataka.
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg"
            data-testid="vision-book-now"
          >
            Book Now <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default VisionMission;
